import React from 'react';
import { CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';

interface TestResult {
  input: string;
  expectedOutput: string;
  actualOutput: string;
  passed: boolean;
  isHidden?: boolean;
}

interface TestResultsProps {
  results: TestResult[];
  showHidden: boolean;
  onToggleHidden: () => void;
}

export default function TestResults({ results, showHidden, onToggleHidden }: TestResultsProps) {
  const visibleResults = showHidden 
    ? results 
    : results.filter(r => !r.isHidden);

  const passedCount = visibleResults.filter(r => r.passed).length;
  const totalTests = visibleResults.length;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Test Results</h3>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-600">
            Passed: {passedCount}/{totalTests}
          </span>
          <button
            onClick={onToggleHidden}
            className="flex items-center text-purple-600 hover:text-purple-700"
          >
            {showHidden ? (
              <>
                <EyeOff className="w-4 h-4 mr-1" />
                Hide Hidden Tests
              </>
            ) : (
              <>
                <Eye className="w-4 h-4 mr-1" />
                Show Hidden Tests
              </>
            )}
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {visibleResults.map((result, index) => (
          <div 
            key={index}
            className={`p-4 rounded-lg border ${
              result.passed ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <div className="flex items-center">
                  {result.passed ? (
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500 mr-2" />
                  )}
                  <span className="font-medium">Test Case {index + 1}</span>
                </div>
                <div className="text-sm">
                  <p><span className="font-medium">Input:</span> {result.input}</p>
                  <p><span className="font-medium">Expected:</span> {result.expectedOutput}</p>
                  {!result.passed && (
                    <p><span className="font-medium">Actual:</span> {result.actualOutput}</p>
                  )}
                </div>
              </div>
              {result.isHidden && (
                <span className="text-xs bg-gray-100 px-2 py-1 rounded">Hidden</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
} 